import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { buildQueryString } from 'src/lib/utilities/api/http';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';
import { RoleManagerRoles } from '../assignment-manager/role-manager-team.model';
import { FocusQueueOptionModel } from './focus-queue-options.model';
import { FocusQueueReportingModel } from './focus-queue-reporting.model';
import {
	WorkQueueEntityArgument,
	WorkQueueEntityModel,
} from './work-queue-entity.model';
import {
	FocusQueueArgument,
	WorkQueueArgument,
	WorkQueueModel,
	WorkQueueTypes,
} from './work-queue.model';

@Injectable({
	providedIn: 'root',
})
export class WorkQueueService {
	constructor(private httpClient: HttpClient) {}

	/**************/
	/*** Queues ***/
	/**************/

	public getQueues = (
		queueOwnerUid: number,
		type: WorkQueueTypes,
	): Observable<WorkQueueModel[]> => {
		return this.httpClient
			.get<WorkQueueModel[]>(`/api/v1/queues/${queueOwnerUid}/${type}`)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueModel, r), {
					errorCode: 'EE9A097E',
				}),
			);
	};

	public createQueue = (
		queueOwnerUid: number,
		type: WorkQueueTypes,
		args: WorkQueueArgument | FocusQueueArgument,
	): Observable<WorkQueueModel> => {
		return this.httpClient
			.post<WorkQueueModel>(`/api/v1/queues/${queueOwnerUid}/${type}`, args)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueModel, r), {
					errorCode: 'DF3C7B44',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public updateQueue = (
		queueOwnerUid: number,
		queueId: number,
		type: WorkQueueTypes,
		args: WorkQueueArgument,
	): Observable<WorkQueueModel> => {
		return this.httpClient
			.post<WorkQueueModel>(
				`/api/v1/queues/${queueOwnerUid}/${type}/${queueId}`,
				args,
			)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueModel, r), {
					errorCode: '1ADDC849',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteQueue = (
		queueOwnerUid: number,
		queueId: number,
		type: WorkQueueTypes,
	): Observable<WorkQueueModel[]> => {
		return this.httpClient
			.delete<
				WorkQueueModel[]
			>(`/api/v1/queues/${queueOwnerUid}/${type}/${queueId}`)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueModel, r), {
					errorCode: '7EF93D57',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	/**********************/
	/*** Queue Entities ***/
	/**********************/

	public getQueueEntities = (
		queueOwnerUid: number,
		queueId: number,
		type: WorkQueueTypes,
		studentId: number | null = null,
	): Observable<WorkQueueEntityModel[]> => {
		const query = buildQueryString({
			entity_id: studentId,
		});

		return this.httpClient
			.get<
				WorkQueueEntityModel[]
			>(`/api/v1/queues/${queueOwnerUid}/${type}/${queueId}${query}`)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueEntityModel, r), {
					errorCode: '353D18CA',
				}),
			);
	};

	public addEntitiesToQueue = (
		queueOwnerUid: number,
		queueId: number,
		type: WorkQueueTypes,
		args: WorkQueueEntityArgument[],
	): Observable<WorkQueueEntityModel[]> => {
		return this.httpClient
			.post<
				WorkQueueEntityModel[]
			>(`/api/v1/queues/${queueOwnerUid}/${type}/${queueId}/add`, args)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueEntityModel, r), {
					errorCode: 'F9E8854F',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public removeEntitiesFromQueue = (
		queueOwnerUid: number,
		queueId: number,
		type: WorkQueueTypes,
		args: WorkQueueEntityArgument[],
	): Observable<WorkQueueEntityModel[]> => {
		return this.httpClient
			.post<
				WorkQueueEntityModel[]
			>(`/api/v1/queues/${queueOwnerUid}/${type}/${queueId}/remove`, args)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueEntityModel, r), {
					errorCode: 'E6C57C67',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public clearEntitiesFromQueue = (
		queueOwnerUid: number,
		queueId: number,
		type: WorkQueueTypes,
		completedOnly: boolean = false,
	): Observable<WorkQueueEntityModel[]> => {
		let body = {};
		if (completedOnly) {
			body = { completed: completedOnly };
		}
		return this.httpClient
			.post<
				WorkQueueEntityModel[]
			>(`/api/v1/queues/${queueOwnerUid}/${type}/${queueId}/clear`, body)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueEntityModel, r), {
					errorCode: 'FA926798',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public toggleQueueEntityComplete = (
		queueOwnerUid: number,
		queueId: number,
		uid: number,
		completed: boolean,
		type: WorkQueueTypes,
	): Observable<WorkQueueEntityModel[]> => {
		return this.httpClient
			.post<
				WorkQueueEntityModel[]
			>(`/api/v1/queues/${queueOwnerUid}/${type}/${queueId}/user/${uid}`, { completed: completed })
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueEntityModel, r), {
					errorCode: '31159EDB',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteQueueEntity = (
		queueOwnerUid: number,
		queueId: number,
		uid: number,
		type: WorkQueueTypes,
	): Observable<WorkQueueEntityModel[]> => {
		return this.httpClient
			.delete<
				WorkQueueEntityModel[]
			>(`/api/v1/queues/${queueOwnerUid}/${type}/${queueId}/user/${uid}`)
			.pipe(
				mapResponse((r) => plainToInstance(WorkQueueEntityModel, r), {
					errorCode: 'FDAB2E6C',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	/************/
	/*** MISC ***/
	/************/

	public getFOCUSOptions = (
		include_deactivated: number = null,
	): Observable<FocusQueueOptionModel[]> => {
		let qs = '';
		if (include_deactivated !== null) {
			qs = `?include_deactivated=${include_deactivated}`;
		}

		return this.httpClient
			.get<{
				types: FocusQueueOptionModel[];
			}>(`/api/v1/queues/options/focus${qs}`)
			.pipe(
				mapResponse((r) => plainToInstance(FocusQueueOptionModel, r.types), {
					errorCode: 'C210E00B',
				}),
			);
	};

	public getFOCUSReporting = (
		role: RoleManagerRoles,
		date: string,
	): Observable<FocusQueueReportingModel[]> => {
		let qs = null;
		if (date != null) {
			qs = buildQueryString({ date: date });
		}

		return this.httpClient
			.get<{
				group_members: FocusQueueReportingModel[];
			}>(`/api/v1/queues/reporting/focus/${role}${qs ? qs : ''}`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(FocusQueueReportingModel, r.group_members),
					{
						errorCode: '4A82B966',
					},
				),
			);
	};
}
